import Vue from "vue";
import axios from "axios";
import {authApi} from "@/app/services/api.service";
import jwtService from "@/app/services/jwt.service";
import store from "@/store";
import abilities from "@/app/services/abilities";
import {
  bumpSessionRevision,
  mergeUserProfile,
  persistRefreshTokenExpiryTime,
} from "@/app/services/tokenRefresh";

function syncAbilities(userDetails) {
  if (!userDetails || !userDetails.roles) {
    return;
  }

  const permissions = [];
  userDetails.roles.forEach((role) => {
    role.permissions.forEach((permission) => {
      permissions.push({
        action: permission.permission.replaceAll(" ", ""),
        resource: permission.group,
      });
    });
  });
  sessionStorage.setItem("abilities", JSON.stringify(permissions));
  abilities.update(permissions);
}

function syncSessionAcrossTabs() {
  localStorage.setItem("sessionStorage", JSON.stringify(sessionStorage));
  localStorage.removeItem("sessionStorage");
}

function stripAuthorizationHeader(config) {
  if (!config.headers) {
    return config;
  }

  if (typeof config.headers.delete === "function") {
    config.headers.delete("Authorization");
    config.headers.delete("authorization");
  } else {
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }

  return config;
}

let refreshInFlight = false;

export function isRefreshInFlight() {
  return refreshInFlight;
}

const refreshClient = authApi();
refreshClient.interceptors.request.use(stripAuthorizationHeader);
refreshClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        bumpSessionRevision();
        localStorage.clear();
        sessionStorage.clear();
        alert("session expired please login again to continue");
        window.location.reload();
      }
      throw error;
    }
);

async function postRefreshToken(refreshToken) {
  return refreshClient.post("auth/api/auth/refresh-token", {refreshToken});
}

export async function refreshSessionToken(refreshToken) {
  if (refreshInFlight) {
    return null;
  }

  refreshInFlight = true;

  try {
    const response = await postRefreshToken(refreshToken);
    const data = response.data;
    const result = data.data || data;

    const newAccessToken = result.accessToken || result.token;
    const newRefreshToken = result.refreshToken || result.tokenRefresh;
    const userDetails = result.user;

    if (newAccessToken) {
      jwtService.saveToken(newAccessToken);
      axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
    }

    if (newRefreshToken) {
      sessionStorage.setItem("tokenRefresh", newRefreshToken);
    }

    persistRefreshTokenExpiryTime(result.refreshTokenExpiryTime);
    if (userDetails && userDetails.refreshTokenExpiryTime) {
      persistRefreshTokenExpiryTime(userDetails.refreshTokenExpiryTime);
    }

    if (userDetails) {
      const mergedUser = mergeUserProfile(jwtService.getUser(), userDetails);
      jwtService.saveUser(mergedUser);
      Vue.prototype.$userProfile = mergedUser;
      store.commit("setCurrentUserProfile", mergedUser);
      syncAbilities(mergedUser);
    }

    syncSessionAcrossTabs();
    bumpSessionRevision();

    return result;
  } finally {
    refreshInFlight = false;
  }
}

export default {refreshSessionToken, isRefreshInFlight};
